import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import heroVideo from "@/assets/hero-video.mp4";
import heroPoster from "@/assets/hero-poster.jpg";

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string | null;
  primary_button_text: string | null;
  primary_button_link: string | null;
  secondary_button_text: string | null;
  secondary_button_link: string | null;
  media_type: "video" | "image";
  media_url: string | null;
  poster_image_url: string | null;
  video_start_time?: number;
  video_end_time?: number | null;
  mobile_video_url: string | null;
  mobile_poster_url: string | null;
  mobile_video_source_type?: "upload" | "youtube" | "external";
  mobile_video_start_time?: number;
  mobile_video_end_time?: number | null;
  display_order: number;
  video_source_type?: "upload" | "youtube" | "external";
}

// Helper to extract YouTube video ID
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Get YouTube thumbnail URL
const getYouTubeThumbnail = (url: string): string | null => {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
};

export function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { data: slides = [] } = useQuery({
    queryKey: ["cms-hero-slides"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_hero_slides")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as HeroSlide[];
    },
  });

  // Fallback slide when no CMS content exists
  const fallbackSlide: HeroSlide = {
    id: "fallback",
    title: "Crafting Visual Stories That **Captivate**",
    subtitle: "From weddings to brand campaigns, we create cinematic experiences that elevate your moments into timeless art.",
    primary_button_text: "View Portfolio",
    primary_button_link: "/portfolio",
    secondary_button_text: "Request a Quote",
    secondary_button_link: "/contact",
    media_type: "video",
    media_url: heroVideo,
    poster_image_url: heroPoster,
    mobile_video_url: null,
    mobile_poster_url: null,
    display_order: 0,
  };

  const activeSlides = slides.length > 0 ? slides : [fallbackSlide];
  const slide = activeSlides[currentSlide] || activeSlides[0];

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying || activeSlides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, activeSlides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 15 seconds
    setTimeout(() => setIsAutoPlaying(true), 15000);
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % activeSlides.length);
  }, [currentSlide, activeSlides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + activeSlides.length) % activeSlides.length);
  }, [currentSlide, activeSlides.length, goToSlide]);

  const scrollToServices = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  // Parse title for gradient text (text between ** becomes gold)
  const renderTitle = (title: string) => {
    if (title.includes("**")) {
      const parts = title.split("**");
      return (
        <>
          {parts[0]}
          <span className="text-gradient-gold">{parts[1]}</span>
          {parts[2] || ""}
        </>
      );
    }
    return title;
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Media */}
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background z-10" />
          
          {slide.media_type === "video" ? (
            slide.video_source_type === "youtube" && slide.media_url ? (
              // YouTube embed with autoplay
              <>
                <img
                  src={slide.poster_image_url || getYouTubeThumbnail(slide.media_url) || heroPoster}
                  alt={slide.title}
                  className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
                />
                <iframe
                  src={`https://www.youtube.com/embed/${extractYouTubeId(slide.media_url)}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(slide.media_url)}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  style={{ transform: 'scale(1.2)', transformOrigin: 'center center' }}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={slide.title}
                />
              </>
            ) : (
              // Regular video (upload or external) with responsive support
              <>
                {/* Desktop video (16:9) - hidden on mobile if mobile video exists */}
                <video
                  key={`desktop-${slide.media_url}`}
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={slide.poster_image_url || heroPoster}
                  className={`absolute inset-0 w-full h-full object-cover animate-ken-burns ${
                    isMobile && slide.mobile_video_url ? 'hidden' : ''
                  }`}
                  onError={(e) => {
                    e.currentTarget.poster = heroPoster;
                  }}
                >
                  <source src={slide.media_url || heroVideo} type="video/mp4" />
                </video>
                
                {/* Mobile video (9:16) - shown only on mobile when available */}
                {isMobile && slide.mobile_video_url && (
                  slide.mobile_video_source_type === "youtube" ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${extractYouTubeId(slide.mobile_video_url)}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(slide.mobile_video_url)}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                      style={{ transform: 'scale(1.5)', transformOrigin: 'center center' }}
                      allow="autoplay; encrypted-media"
                      title={slide.title}
                    />
                  ) : (
                    <video
                      ref={mobileVideoRef}
                      key={`mobile-${slide.mobile_video_url}`}
                      autoPlay
                      muted
                      loop
                      playsInline
                      poster={slide.mobile_poster_url || slide.poster_image_url || heroPoster}
                      className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
                      onError={(e) => {
                        e.currentTarget.poster = slide.mobile_poster_url || heroPoster;
                      }}
                    >
                      <source src={slide.mobile_video_url} type="video/mp4" />
                    </video>
                  )
                )}
              </>
            )
          ) : (
            <img
              src={slide.media_url || heroPoster}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="container mx-auto px-6 relative z-20">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={`content-${slide.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="inline-block text-primary text-sm font-medium tracking-[0.3em] uppercase mb-6">
                  Luxury Cinematic Videography
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold leading-tight mb-6"
              >
                {renderTitle(slide.title)}
              </motion.h1>

              {slide.subtitle && (
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
                >
                  {slide.subtitle}
                </motion.p>
              )}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                {slide.primary_button_text && (
                  <Button asChild size="lg" className="text-base px-8">
                    <Link to={slide.primary_button_link || "/portfolio"}>
                      <Play className="mr-2 h-4 w-4" />
                      {slide.primary_button_text}
                    </Link>
                  </Button>
                )}
                {slide.secondary_button_text && (
                  <Button asChild variant="outline" size="lg" className="text-base px-8">
                    <Link to={slide.secondary_button_link || "/contact"}>
                      {slide.secondary_button_text}
                    </Link>
                  </Button>
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Slide Navigation - Only show if multiple slides */}
      {activeSlides.length > 1 && (
        <>
          {/* Arrow Navigation */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-background/20 backdrop-blur-sm text-foreground/80 hover:bg-background/40 hover:text-foreground transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-background/20 backdrop-blur-sm text-foreground/80 hover:bg-background/40 hover:text-foreground transition-all"
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>

          {/* Dot Navigation */}
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            {activeSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? "bg-primary w-6"
                    : "bg-foreground/30 hover:bg-foreground/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Scroll Indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        onClick={scrollToServices}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-primary transition-colors z-20"
        aria-label="Scroll to services"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <ChevronDown size={32} />
        </motion.div>
      </motion.button>
    </section>
  );
}
